/**
 * Minimal markdown parser for the legal documents (privacy policy, terms,
 * imprint). Supports exactly the subset those documents use:
 *
 *   blocks:  # headings, paragraphs (single newlines are line breaks),
 *            - / 1. lists, | pipe | tables |, > blockquotes, --- rules
 *   inline:  **bold**, *italic* / _italic_, `code`, [text](href),
 *            bare https:// URLs and e-mail addresses
 *
 * It produces a plain data tree; MarkdownContent renders it with React
 * elements and design-system atoms. Raw HTML is never interpreted — it stays
 * literal text — so no markup or script can be injected through the content.
 */

export type InlineNode =
  | { type: 'text'; text: string }
  | { type: 'strong'; children: InlineNode[] }
  | { type: 'em'; children: InlineNode[] }
  | { type: 'code'; text: string }
  | { type: 'link'; href: string; children: InlineNode[] }
  | { type: 'break' };

export type BlockNode =
  | { type: 'heading'; level: 1 | 2 | 3 | 4 | 5 | 6; children: InlineNode[] }
  | { type: 'paragraph'; children: InlineNode[] }
  | { type: 'list'; ordered: boolean; items: InlineNode[][] }
  | { type: 'table'; header: InlineNode[][]; rows: InlineNode[][][] }
  | { type: 'blockquote'; children: BlockNode[] }
  | { type: 'hr' };

// ─── Links ────────────────────────────────────────────────────────────────

/**
 * Returns the href when it is safe to render as a link (http(s), mailto,
 * same-site path or fragment), otherwise null — `javascript:`, `data:` and
 * protocol-relative URLs are never linked.
 */
export function safeHref(raw: string): string | null {
  const href = raw.trim();
  if (/^https?:\/\/[^\s]+$/i.test(href)) return href;
  if (/^mailto:[^\s]+$/i.test(href)) return href;
  if (/^\/(?!\/)[^\s]*$/.test(href)) return href;
  if (/^#[^\s]*$/.test(href)) return href;
  return null;
}

export function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

// ─── Inline ───────────────────────────────────────────────────────────────

interface InlineRule {
  re: RegExp;
  build: (m: RegExpExecArray) => InlineNode;
}

const INLINE_RULES: InlineRule[] = [
  { re: /`([^`]+)`/, build: (m) => ({ type: 'code', text: m[1] }) },
  {
    re: /\[([^\]]+)\]\(([^)\s]+)\)/,
    build: (m) => {
      const href = safeHref(m[2]);
      return href
        ? { type: 'link', href, children: parseInline(m[1]) }
        : { type: 'text', text: m[1] };
    },
  },
  { re: /\*\*(.+?)\*\*/, build: (m) => ({ type: 'strong', children: parseInline(m[1]) }) },
  { re: /\*([^*\s][^*]*?)\*/, build: (m) => ({ type: 'em', children: parseInline(m[1]) }) },
  { re: /(?<![\w])_([^_\s][^_]*?)_(?![\w])/, build: (m) => ({ type: 'em', children: parseInline(m[1]) }) },
  {
    re: /https?:\/\/[^\s<>()]*[^\s<>().,;:!?'"“”„]/,
    build: (m) => ({ type: 'link', href: m[0], children: [{ type: 'text', text: m[0] }] }),
  },
  {
    re: /[\w.%+-]+@[\w-]+\.[\w.-]*[A-Za-z]{2}/,
    build: (m) => ({ type: 'link', href: `mailto:${m[0]}`, children: [{ type: 'text', text: m[0] }] }),
  },
];

export function parseInline(text: string): InlineNode[] {
  const out: InlineNode[] = [];
  let rest = text;
  while (rest.length > 0) {
    let best: { index: number; length: number; node: InlineNode } | null = null;
    for (const rule of INLINE_RULES) {
      const m = rule.re.exec(rest);
      if (m && (best === null || m.index < best.index)) {
        best = { index: m.index, length: m[0].length, node: rule.build(m) };
      }
    }
    if (best === null) {
      out.push({ type: 'text', text: rest });
      break;
    }
    if (best.index > 0) out.push({ type: 'text', text: rest.slice(0, best.index) });
    out.push(best.node);
    rest = rest.slice(best.index + best.length);
  }
  return out;
}

function parseLines(lines: string[]): InlineNode[] {
  const out: InlineNode[] = [];
  lines.forEach((line, i) => {
    if (i > 0) out.push({ type: 'break' });
    out.push(...parseInline(line.trim()));
  });
  return out;
}

// ─── Blocks ───────────────────────────────────────────────────────────────

const HEADING = /^(#{1,6})\s+(.+?)\s*#*\s*$/;
const HR = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/;
const QUOTE = /^\s*>\s?(.*)$/;
const UL_ITEM = /^\s*[-*+]\s+(.*)$/;
const OL_ITEM = /^\s*\d+[.)]\s+(.*)$/;
const TABLE_SEP_CELL = /^\s*:?-{2,}:?\s*$/;

function splitRow(line: string): string[] {
  let row = line.trim();
  if (row.startsWith('|')) row = row.slice(1);
  if (row.endsWith('|')) row = row.slice(0, -1);
  return row.split('|').map((cell) => cell.trim());
}

function isTableSeparator(line: string): boolean {
  return line.includes('-') && splitRow(line).every((cell) => TABLE_SEP_CELL.test(cell));
}

function isTableStart(lines: string[], i: number): boolean {
  return lines[i].trim().startsWith('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1]);
}

function startsBlock(lines: string[], i: number): boolean {
  const line = lines[i];
  return HEADING.test(line) || HR.test(line) || QUOTE.test(line) || UL_ITEM.test(line) || OL_ITEM.test(line) || isTableStart(lines, i);
}

export function parseMarkdown(source: string): BlockNode[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const blocks: BlockNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === '') {
      i += 1;
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length as 1 | 2 | 3 | 4 | 5 | 6, children: parseInline(heading[2]) });
      i += 1;
      continue;
    }

    if (HR.test(line)) {
      blocks.push({ type: 'hr' });
      i += 1;
      continue;
    }

    if (QUOTE.test(line)) {
      const inner: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i])) {
        inner.push((QUOTE.exec(lines[i]) as RegExpExecArray)[1]);
        i += 1;
      }
      blocks.push({ type: 'blockquote', children: parseMarkdown(inner.join('\n')) });
      continue;
    }

    if (isTableStart(lines, i)) {
      const header = splitRow(line).map(parseInline);
      i += 2;
      const rows: InlineNode[][][] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        const cells = splitRow(lines[i]);
        rows.push(header.map((_, c) => parseInline(cells[c] ?? '')));
        i += 1;
      }
      blocks.push({ type: 'table', header, rows });
      continue;
    }

    const ordered = OL_ITEM.test(line);
    if (ordered || UL_ITEM.test(line)) {
      const itemRe = ordered ? OL_ITEM : UL_ITEM;
      const items: string[][] = [];
      while (i < lines.length && lines[i].trim() !== '') {
        const item = itemRe.exec(lines[i]);
        if (item) items.push([item[1]]);
        else if (/^\s+\S/.test(lines[i]) && items.length > 0) items[items.length - 1].push(lines[i]);
        else break;
        i += 1;
      }
      blocks.push({ type: 'list', ordered, items: items.map(parseLines) });
      continue;
    }

    const para: string[] = [];
    while (i < lines.length && lines[i].trim() !== '' && (para.length === 0 || !startsBlock(lines, i))) {
      para.push(lines[i]);
      i += 1;
    }
    blocks.push({ type: 'paragraph', children: parseLines(para) });
  }

  return blocks;
}

/** Plain text of an inline tree (for labels). */
export function inlineText(nodes: InlineNode[]): string {
  return nodes
    .map((n) => {
      switch (n.type) {
        case 'text':
        case 'code':
          return n.text;
        case 'break':
          return ' ';
        default:
          return inlineText(n.children);
      }
    })
    .join('');
}
